import { Box, IconButton, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

interface BrowseNavigationBarProps {
  /** Whether backward navigation is available (same-page or cross-page). */
  readonly canPrev: boolean;
  /** Whether forward navigation is available (same-page or cross-page). */
  readonly canNext: boolean;
  readonly positionLabel: string | null;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  /** True while a cross-page boundary fetch is in flight — disables both arrows. */
  readonly loading?: boolean;
}

/**
 * Prev/next navigation arrows with position indicator for stepping
 * through catalog items on the detail page.
 * Uses canPrev/canNext (not prevId/nextId) so cross-page boundary navigation
 * keeps arrows active even when there is no same-page id (GL#39).
 */
export const BrowseNavigationBar = ({
  canPrev,
  canNext,
  positionLabel,
  onPrev,
  onNext,
  loading = false,
}: BrowseNavigationBarProps) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <IconButton
      onClick={onPrev}
      disabled={!canPrev || loading}
      size="small"
      aria-label="Previous item"
    >
      <ChevronLeft />
    </IconButton>
    {positionLabel && (
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          minWidth: 50,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
        }}
      >
        {positionLabel}
      </Typography>
    )}
    <IconButton
      onClick={onNext}
      disabled={!canNext || loading}
      size="small"
      aria-label="Next item"
    >
      <ChevronRight />
    </IconButton>
  </Box>
);
